import React, { useState, useEffect } from 'react';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  Typography,
  Button,
  Container,
  Grid,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import Navbar from './Navbar';
import Content from './Content';
import { theme } from '../../../style/theme';
import { motion } from 'framer-motion';
import Pipeline from './Pipeline';
import SeamlessData from './SeamlessData';
import MidSection from './MidSection';
import Works from './Works';
import Reviews from './Reviews';
import Plans from './Plans';
import Footer from './Footer';
import { Navigate, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import connectSync from "../../../Images/connectSync.png";


const MainpageContent = () => {
  const navigate = useNavigate();
  const authToken = Cookies.get('authToken');
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const heroSection = document.getElementById('hero-section');
      if (heroSection) {
        const rect = heroSection.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
      }
    };

    const heroSection = document.getElementById('hero-section');
    if (heroSection) {
      heroSection.addEventListener('mousemove', handleMouseMove);
      return () => heroSection.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  if (authToken) {
    return <Navigate to="/userdashboard" replace />;
  }


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '100vw',
          overflowX: 'hidden',
          bgcolor: 'background.default',
        }}
      >
        {/* Navbar */}
        <Navbar />

        {/* Hero Section with Grid Background */}
        <Box
          id="hero-section"
          sx={{
            width: '100vw',
            minHeight: '100vh',
            background: '#3044A9',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            cursor: 'none',
          }}
        >
          {/* Enhanced Grid Pattern Background - Full Coverage */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
            `,
              backgroundSize: '80px 80px',
              opacity: 0.25,
            }}
          />

          {/* Cursor Following Grid Highlight */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
            `,
              backgroundSize: '80px 80px',
              maskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              WebkitMaskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
            }}
          />

          {/* Fine Grid Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
            `,
              backgroundSize: '20px 20px',
              opacity: 0.2,
            }}
          />

          {/* Cursor Following Fine Grid */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px)
            `,
              backgroundSize: '20px 20px',
              maskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              WebkitMaskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
              transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
            }}
          />

          {/* Cursor Glow Effect */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 3,
              background: `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.06) 0%, transparent 70%)`,
              transition: 'background 0.2s ease-out',
              pointerEvents: 'none',
            }}
          />

          {/* Animated Grid Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              backgroundImage: `
              radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.06) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)
            `,
              animation: 'gridPulse 8s ease-in-out infinite',
              '@keyframes gridPulse': {
                '0%, 100%': {
                  opacity: 0.3,
                },
                '50%': {
                  opacity: 0.6,
                },
              },
            }}
          />

          {/* Depth and Glow Effects */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              background: `
              radial-gradient(ellipse at center, rgba(255, 255, 255, 0.02) 0%, transparent 70%),
              linear-gradient(45deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
              linear-gradient(-45deg, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
            `,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '10%',
                left: '10%',
                right: '10%',
                bottom: '10%',
                background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                animation: 'float 6s ease-in-out infinite',
              },
              '@keyframes float': {
                '0%, 100%': {
                  transform: 'translateY(0px) scale(1)',
                  opacity: 0.3,
                },
                '50%': {
                  transform: 'translateY(-10px) scale(1.05)',
                  opacity: 0.5,
                },
              },
            }}
          />

          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 10, py: 8 }}>
            <Grid container spacing={4} alignItems="center">
              {/* Left Side - Content */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                  {/* ETL Tool Badge */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: '#fff',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '50px',
                      px: 2,
                      py: 1,
                      mb: 4,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <SettingsIcon
                      sx={{
                        color: '#3046A9',
                        fontSize: '16px',
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#3046A9',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                      }}
                    >
                      ETL Tool
                    </Typography>
                  </Box>

                  {/* Main Heading */}
                  <Typography
                    variant="h1"
                    component={motion.h1}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem', lg: '4rem' },
                      mb: 3,
                      color: '#ffffff',
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Seamless Data
                    <br />
                    Integration Simplified.
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    component={motion.p}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    sx={{
                      fontSize: { xs: '1rem', md: '1.125rem' },
                      mb: 4,
                      color: 'rgba(255, 255, 255, 0.9)',
                      lineHeight: 1.6,
                      fontWeight: 400,
                      maxWidth: '500px',
                    }}
                  >
                    SkyShift simplifies data integration with automated
                    transformation, real-time monitoring, and scalable loading for
                    efficient workflows and insights.
                  </Typography>

                  {/* Action Buttons */}
                  <Box
                    component={motion.div}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                    }}
                  >
                    {/* Get Started Button */}
                    <Button
                      variant="contained"
                      onClick={() => navigate("/login")}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: '12px',
                        backgroundColor: '#ffffff',
                        color: '#4c63d2',
                        textTransform: 'none',
                        minWidth: '140px',
                        boxShadow: '0 4px 20px rgba(255, 255, 255, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: '#f8fafc',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 30px rgba(255, 255, 255, 0.4)',
                        },
                      }}
                    >
                      Get Started
                    </Button>

                    {/* Read Docs Button */}
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/documentation")}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: '12px',
                        color: '#ffffff',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        textTransform: 'none',
                        minWidth: '140px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Read Docs
                    </Button>
                  </Box>
                </motion.div>
              </Grid>

              {/* Right Side - Visual */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: '600px',
                      height: 'auto',
                    }}
                  >
                    <img
                      src={connectSync}
                      alt="Data Integration Visualization"
                      style={{
                        width: '100%',
                        height: 'auto',
                        filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.2))',
                      }}
                    />
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Content />
        <Pipeline />
        <SeamlessData />
        <MidSection />
        <Works />
        <Reviews />
        <Plans />
      </Box>
      <Footer />
    </ThemeProvider>
  );
};

export default MainpageContent;
